import {getAccessToken, getWaffleServerUrlFromID, getSupaServerList} from "./supa-helpers.js";
async function fetchJSON(url, options){ let ok, error; await d3.json(url, options).then(d => ok = d).catch(e => error = e); return {ok, error}; }
const addSlash = (string = "") => string.endsWith("/") ? string : string + "/";


const memo = {
  datasetInfoByWS: new Map(),
  waffleStatusByWS: new Map(),
  waffleDatasetConfigByWS: new Map(),
}


export async function getWaffleStatusForServer({id, url} = {}, refresh = false) {
  if (memo.waffleStatusByWS.has(id) && !refresh) return {ok:memo.waffleStatusByWS.get(id)};

  const {ok, error} = await fetchJSON(addSlash(url) + "status/");
  if (error) return {error: "Failed to fetch waffle status: " + error};

  memo.waffleStatusByWS.set(id, ok);
  return {ok, error};
}

export async function getWaffleDatasetConfig({id, url} = {}, token, refresh = false) {
  if (memo.waffleDatasetConfigByWS.has(id) && !refresh) return {ok: memo.waffleDatasetConfigByWS.get(id)};

  if(!token) token = await getAccessToken();
  const {ok: dsConfig, error} = await fetchJSON(addSlash(url) + "info/", {headers: {Authorization: `Bearer ${token}`, Accept: "application/json"}});
  if (error) return {error: "Failed to fetch waffle datset config: " + error};

  memo.waffleDatasetConfigByWS.set(id, {
    datasetControlList: dsConfig.datasetControlList.map(ds => ({...ds, host: "waffle", url: "https://github.com/" + ds.githubRepoId })),
    datasetBranchCommitMapping: dsConfig.datasetBranchCommitMapping,
    commitTimeStamp: dsConfig.commitTimeStamp,
    commitAuthor: dsConfig.commitAuthor,
  });

  return {ok: memo.waffleDatasetConfigByWS.get(id), error};
}


export async function getWaffleDatasetInfoForServer({id, url} = {}, token, refresh = false){
    if (memo.datasetInfoByWS?.has(id) && !refresh) return {ok: memo.datasetInfoByWS.get(id)};

    if(!token) token = await getAccessToken();
    const {ok: config, error} = await getWaffleDatasetConfig({id, url}, token, refresh);

    if (error || !config?.datasetControlList?.length)
      return {error: "No datasets found on server, maybe due to insufificient access rights"};
    const promises = []
    config.datasetControlList.forEach(dataset => dataset.branches.forEach(branch => promises.push({
      slug: dataset.slug,
      branch,
      promise: fetchJSON(`${addSlash(url)}info/${dataset.slug}/${branch}`, {headers: {Authorization: `Bearer ${token}`, Accept: "application/json"}})
    }) ))
  
    const resolved = await Promise.all(promises.map(m => m.promise));
    if(resolved.every(r => r.error)) return {error: "Failed to fetch dataset info: " + resolved.map(r => r.error).join("; ")};
    
    const result = {};
    for(let i in resolved) {
      result[promises[i].slug + " " + promises[i].branch] = resolved[i].ok || resolved[i].error;
    }

    memo.datasetInfoByWS.set(id, result);
    return {ok: memo.datasetInfoByWS.get(id)};
}

export async function runWaffleSyncACL(id) {
  // no specific id -- sync all servers!
  if (!id) {
    const {ok: servers, error} = await getSupaServerList();
    if (error) return { error };
    const promises = await Promise.all(servers.map(server => runWaffleSyncACL(server.id)));
    if (promises.every(p => p.error)) return { error: promises.map(p => p.error) };
    return {ok: promises.map(p => p.ok)}
  }

  const token = await getAccessToken();
  const {ok: url, error} = await getWaffleServerUrlFromID(id);
  if (error) return { error };

  if(!url) return { error: "Url not resolved for " + id }; //the fake __all__ server has no URL, skip it
  return fetchJSON(`${addSlash(url)}synconly/acl/`, {headers: {Authorization: `Bearer ${token}`, Accept: "application/json"}} );
}

export async function runWaffleSyncDCL(url, token) {
  if(!token) token = await getAccessToken();
  if(url) url = addSlash(url);
  return fetchJSON(`${url}synconly/dcl/`, {headers: {Authorization: `Bearer ${token}`, Accept: "application/json"}} );
}

export async function runWaffleSync(url, slug, branch, token) {
  if(!token) token = await getAccessToken();
  if(url) url = addSlash(url);
  if (url && slug && branch) return fetchJSON(`${url}sync/${slug}/${branch}?skipValidation=true`, {headers: {Authorization: `Bearer ${token}`, Accept: "application/json"}} );
  if (url && slug) return fetchJSON(`${url}sync/${slug}?skipValidation=true`, {headers: {Authorization: `Bearer ${token}`, Accept: "application/json"}} );
  if (url) return fetchJSON(`${url}sync?skipValidation=true`, {headers: {Authorization: `Bearer ${token}`, Accept: "application/json"}} );
  return {error: "bad params supplied to sync " + JSON.stringify({url, slug, branch})};
}

export async function getWaffleSyncProgress(url) {
  return fetchJSON(addSlash(url) + "syncprogress");
}

export async function runWaffleValidate(url, slug, branch, token) {
  if(!token) token = await getAccessToken();
  if(url) url = addSlash(url);
  const path = branch ? `validate/${slug}/${branch}` : `validate/${slug}`;
  return fetchJSON(`${url}${path}`, {headers: {Authorization: `Bearer ${token}`, Accept: "application/json"}});
}

export async function getWaffleProgress(url) {
  return fetchJSON(addSlash(url) + "progress");
}

export async function getWaffleIsValid(url, slug, branch, token) {
  if(!token) token = await getAccessToken();
  if(url) url = addSlash(url);
  const path = branch ? `isvalid/${slug}/${branch}` : `isvalid/${slug}`;
  return fetchJSON(`${url}${path}`, {headers: {Authorization: `Bearer ${token}`, Accept: "application/json"}});
}

export async function getWaffleEventFacets(url, token, params = {}) {
  if (!token) token = await getAccessToken();
  const qs = new URLSearchParams(params).toString();
  const path = `events/facets${qs ? "?" + qs : ""}`;
  return fetchJSON(addSlash(url) + path, {headers: {Authorization: `Bearer ${token}`, Accept: "application/json"}});
}

export async function getWaffleEvents(url, token, params = {}) {
  if (!token) token = await getAccessToken();
  const qs = new URLSearchParams(params).toString();
  const path = `events${qs ? "?" + qs : ""}`;
  return fetchJSON(addSlash(url) + path, {headers: {Authorization: `Bearer ${token}`, Accept: "application/json"}});
}


export function timediff(time, emo = true, ago = false, longlabels = true){

  const parsed = new Date(time)
  const now = new Date();

  let attempt;

  const
    YEAR = longlabels ? " year" : "yr",
    MONTH = longlabels ? " month" : "mo",
    WEEK = longlabels ? " week" : "w",
    DAY = longlabels ? " day" : "d",
    HOUR = longlabels ? " hour" : "h",
    MINUTE = longlabels ? " min" : "m",
    SECOND = longlabels ? " sec" : "s";

  function diff(time, now, counter, limit, fraction, firstLabel, secondLabel, emoSymbol){
    const count = counter.count(time, now);
    if (count <= limit) return false;
    const first = Math.floor(count / fraction);
    const second = count % fraction;
    const firstS = longlabels && (""+first).at(-1) !== "1" && firstLabel.length > 1 ? "s":"";
    const secondS = longlabels && (""+second).at(-1) !== "1" && secondLabel.length > 1 ? "s":"";
    return `${emo ? emoSymbol + " " : ""}${first}${firstLabel}${firstS} ${second?second:""}${second?secondLabel:""}${second?secondS:""} ${ago ? "ago":""}`
  }

  attempt = diff(parsed, now, d3.timeMonth, 12, 12, YEAR, MONTH, "🌚") // over a year ago
  if (attempt) return attempt;

  attempt = diff(parsed, now, d3.timeWeek, 6*4, 4, MONTH, WEEK, "🌑") // over 6x4 weeks ago
  if (attempt) return attempt;

  attempt = diff(parsed, now, d3.timeWeek, 4, 4, MONTH, WEEK, "🌘") // over 4 weeks ago
  if (attempt) return attempt;

  attempt = diff(parsed, now, d3.timeDay, 2*7, 7, WEEK, DAY, "🌗") // over 2 weeks ago
  if (attempt) return attempt;

  attempt = diff(parsed, now, d3.timeDay, 7, 7, WEEK, DAY, "🌖") // over 7 days ago
  if (attempt) return attempt;

  attempt = diff(parsed, now, d3.timeHour, 24, 24, DAY, HOUR, "🌕") // over 24 hours ago
  if (attempt) return attempt;

  attempt = diff(parsed, now, d3.timeMinute, 60, 60, HOUR, MINUTE, "🔥") // over an hour ago
  if (attempt) return attempt;

  attempt = diff(parsed, now, d3.timeSecond, 60, 60, MINUTE, SECOND, "🔥🔥") // over 60s ago
  if (attempt) return attempt;

  const diffS = d3.timeSecond.count(parsed, now)
  if (diffS > 1) return (emo ? "🔥🔥🔥 ":"") + diffS + "s" + (ago ? " ago":"") // under a minute ago
  return ago ? "just now" : "0s"; // 0-1 seconds
}


